/**
 * lib/applicationSubmission.ts
 * ------------------------------------------------------------------
 * THE FORM AND THE SECURITY RULE HAVE TO AGREE.
 *
 * A parent completed six steps, pressed Submit, and nothing happened.
 * No application reached the school, no email reached them, and the
 * button sat on "Submitting…". Two things were true at once:
 *
 *   THE RULE IS STRICTER THAN THE FORM. `isValidApplicationSubmission`
 *   in firestore.rules caps lengths the form never checked — notes at
 *   2000 characters, the conditions list at 40. When the rule refuses,
 *   Firestore says "Missing or insufficient permissions", which tells a
 *   parent nothing and tells the office less. The form must not be able
 *   to build a payload the rule will refuse.
 *
 *   THE WRITE CAN HANG FOREVER. `addDoc` resolves on server
 *   acknowledgement, so a blocked connection leaves a promise that
 *   never settles and never rejects — no error, no timeout, no record,
 *   and a button that says "Submitting…" until the tab is closed.
 *
 * This module is that agreement, written once, in plain language, and
 * checked before anything is sent. Every limit here mirrors
 * firestore.rules — CHANGE ONE, CHANGE BOTH.
 * ------------------------------------------------------------------ */

/** Mirrors `isValidApplicationSubmission()` in firestore.rules. */
export const SUBMISSION_LIMITS = {
  name: 80,
  dateOfBirth: 12,
  applyingForForm: 20,
  applyingForYearLength: 4,
  addressMin: 5,
  addressMax: 400,
  nationality: 60,
  guardiansMin: 1,
  guardiansMax: 6,
  clientToken: 80,
  acceptedTermsAt: 40,
  conditionsMin: 1,
  conditionsMax: 40,
  notes: 2000,
} as const;

export interface SubmissionShape {
  status?: string;
  referenceNumber?: string;
  firstName?: string;
  surname?: string;
  dateOfBirth?: string;
  gender?: string;
  applyingForForm?: string;
  applyingForYear?: string;
  address?: string;
  nationality?: string;
  guardians?: unknown[];
  clientToken?: string;
  acceptedTerms?: boolean;
  acceptedTermsAt?: string;
  acceptedConditions?: unknown[];
  notes?: string;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Why the security rule would refuse this submission, in words a parent
 * can act on — or null when it will be accepted.
 *
 * Ordered to match the rule so the two stay legibly in step. Returns the
 * FIRST problem only: a parent fixes one field at a time, and the form
 * already marks its own per-field errors in red.
 */
export function submissionShapeProblem(d: SubmissionShape): string | null {
  const L = SUBMISSION_LIMITS;

  if (d.status !== 'submitting') return 'This application is not in a state that can be submitted.';
  if (d.referenceNumber) return 'This application already has a reference number.';

  if (!str(d.firstName).trim()) return 'First name is required.';
  if (str(d.firstName).length > L.name) return `First name must be ${L.name} characters or fewer.`;
  if (!str(d.surname).trim()) return 'Surname is required.';
  if (str(d.surname).length > L.name) return `Surname must be ${L.name} characters or fewer.`;

  if (str(d.dateOfBirth).length > L.dateOfBirth) return 'Date of birth is not in a valid format.';
  if (d.gender !== 'M' && d.gender !== 'F') return 'Please select gender.';

  if (str(d.applyingForForm).length > L.applyingForForm) {
    return 'The form being applied for is not valid.';
  }
  if (str(d.applyingForYear).length !== L.applyingForYearLength) {
    return 'Year must be 4 digits.';
  }

  const address = str(d.address);
  if (address.length < L.addressMin) return 'Address is too short.';
  if (address.length > L.addressMax) return `Address must be ${L.addressMax} characters or fewer.`;

  if (str(d.nationality).length > L.nationality) {
    return `Nationality must be ${L.nationality} characters or fewer.`;
  }

  const guardians = Array.isArray(d.guardians) ? d.guardians : [];
  if (guardians.length < L.guardiansMin) return 'At least one parent or guardian is required.';
  if (guardians.length > L.guardiansMax) {
    return `An application can list at most ${L.guardiansMax} parents or guardians.`;
  }

  if (!str(d.clientToken) || str(d.clientToken).length > L.clientToken) {
    return 'This form could not be identified. Please reload the page and try again.';
  }

  /* THE ACKNOWLEDGEMENT IS NOT OPTIONAL — the rule refuses without it,
     so the form must not offer a way past it either. */
  if (d.acceptedTerms !== true) {
    return 'Please read and accept the Conditions of Admission before submitting.';
  }
  if (!str(d.acceptedTermsAt) || str(d.acceptedTermsAt).length > L.acceptedTermsAt) {
    return 'The acceptance of the Conditions of Admission could not be recorded.';
  }

  const conditions = Array.isArray(d.acceptedConditions) ? d.acceptedConditions : [];
  if (conditions.length < L.conditionsMin) {
    return 'The Conditions of Admission could not be loaded. Please reload the page and try again.';
  }
  if (conditions.length > L.conditionsMax) {
    return 'The Conditions of Admission are too long to record. Please contact the admissions office.';
  }

  /* THE ONE THE FORM NEVER CHECKED. The textarea had no limit, so a
     parent could write past the cap and be refused with no explanation. */
  if (str(d.notes).length > L.notes) {
    const over = str(d.notes).length - L.notes;
    return `Additional notes are ${over} character${over === 1 ? '' : 's'} too long `
      + `(the limit is ${L.notes}).`;
  }

  return null;
}

/**
 * A write that never settles is worse than one that fails.
 *
 * `addDoc` resolves only when the server acknowledges. On a connection
 * that is blocked rather than broken — a captive portal, a corporate
 * proxy, a phone that has quietly dropped off data — the promise stays
 * pending indefinitely: nothing throws, so nothing catches, and a form
 * that awaits it never recovers.
 *
 * Racing it against a timer turns silence into a message. The write may
 * still land afterwards, so the message says so rather than claiming the
 * application was lost — telling a parent to re-submit something that
 * did arrive is how duplicates are made.
 */
export const SUBMIT_TIMEOUT_MS = 25000;

export class SubmitTimeout extends Error {
  constructor() {
    super(
      'We could not confirm your application was received — the connection to the school '
      + 'timed out. Please check your internet connection and try again. If you see this more '
      + 'than once, contact the admissions office before re-submitting, in case the first '
      + 'attempt did arrive.',
    );
    this.name = 'SubmitTimeout';
  }
}

export function withSubmitTimeout<T>(p: Promise<T>, ms: number = SUBMIT_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SubmitTimeout()), ms);
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/**
 * Firebase's own wording, translated.
 *
 * "Missing or insufficient permissions" is accurate and useless: a
 * parent reads it as being locked out of something, when what it means
 * is that a field on their form was not acceptable.
 */
export function explainSubmitError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? '';
  const message = (e as { message?: string })?.message ?? '';

  if (e instanceof SubmitTimeout) return message;

  if (code === 'permission-denied' || /insufficient permissions/i.test(message)) {
    return 'The school could not accept this application as filled in. Please check every step '
      + 'for a field marked in red, then try again. If everything looks correct, contact the '
      + 'admissions office and they will capture it for you.';
  }
  if (code === 'unavailable' || /offline|network|failed to get document/i.test(message)) {
    return 'You appear to be offline. Your application has not been sent — please reconnect '
      + 'and press Submit again.';
  }
  if (code === 'resource-exhausted') {
    return 'The school system is busy. Please wait a moment and press Submit again.';
  }
  return message || 'Submission failed. Please try again.';
}
